import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Search, Ticket, CheckCircle2, XCircle, Clock } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { LogoImg } from "@/components/landing/Logo";

export default function AreaCrediti() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/credits/lookup`, { params: { email } });
      setResult(data);
    } catch {
      setError("Errore nella ricerca. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (iso) =>
    iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="min-h-screen bg-alabaster px-6 py-14" data-testid="area-crediti-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <Link to="/" data-testid="area-crediti-logo">
            <LogoImg className="h-12 w-auto" />
          </Link>
          <Link to="/" className="text-sm font-medium text-sub hover:text-moss transition-colors">
            ← Torna alla home
          </Link>
        </div>

        <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight text-moss">
          I tuoi voucher
        </h1>
        <p className="mt-3 text-base text-sub">
          Inserisci l'email usata al momento dell'acquisto per vedere i tuoi voucher
          attivi, quelli già utilizzati e le loro fasce di utilizzo.
        </p>

        <form onSubmit={lookup} className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="credits-lookup-form">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="credits-email-input"
            placeholder="La tua email di acquisto"
            className="flex-1 rounded-2xl border border-line bg-white px-5 py-4 text-base focus:outline-none focus:ring-2 focus:ring-moss/40"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="credits-lookup-button"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-moss px-7 py-4 text-base font-semibold text-alabaster transition-all duration-200 hover:bg-mosslight disabled:opacity-60"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
            Verifica saldo
          </button>
        </form>

        {error && <p className="mt-4 text-sm text-terracotta font-medium" data-testid="credits-error">{error}</p>}

        {result && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-10">
            {result.vouchers.length === 0 ? (
              <div className="rounded-3xl bg-white border border-line p-8 text-center" data-testid="credits-empty">
                <p className="text-sub">Nessun acquisto trovato per questa email.</p>
                <Link to="/#pacchetti" className="mt-3 inline-block text-sm font-semibold text-terracotta hover:underline">
                  Vedi i pacchetti voucher →
                </Link>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="rounded-3xl bg-moss p-6" data-testid="credits-balance-active">
                    <p className="font-serif text-4xl font-bold text-gold">{result.vouchers.filter((v) => !v.redeemed).length}</p>
                    <p className="mt-1 text-xs uppercase tracking-widest font-mono text-alabaster/70">Voucher attivi</p>
                  </div>
                  <div className="rounded-3xl bg-white border border-line p-6" data-testid="credits-balance-used">
                    <p className="font-serif text-4xl font-bold text-sub">{result.vouchers.filter((v) => v.redeemed).length}</p>
                    <p className="mt-1 text-xs uppercase tracking-widest font-mono text-sub">Voucher utilizzati</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {result.vouchers.map((v) => (
                    <div
                      key={v.credit_code}
                      data-testid={`voucher-${v.credit_code}`}
                      className={`rounded-3xl border p-6 flex flex-wrap items-center justify-between gap-4 ${
                        v.redeemed ? "bg-tint border-line opacity-70" : "bg-white border-moss/20"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${v.redeemed ? "bg-line" : "bg-sage"}`}>
                          <Ticket size={20} className={v.redeemed ? "text-sub" : "text-moss"} />
                        </div>
                        <div>
                          <p className="font-mono font-semibold text-lg tracking-wide text-ink">{v.credit_code}</p>
                          <p className="text-xs font-bold uppercase tracking-wider text-terracotta mt-0.5">{v.tier}</p>
                          <p className="text-xs text-sub mt-0.5">
                            {v.package_name} · acquistato il {fmtDate(v.created_at)}
                          </p>
                          <p className="text-xs text-sub mt-0.5">Utilizzabile {v.schedule_label}</p>
                        </div>
                      </div>
                      {v.redeemed ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-line px-4 py-2 text-xs font-bold uppercase tracking-wider text-sub" data-testid={`voucher-status-${v.credit_code}`}>
                          <XCircle size={14} /> Utilizzato{v.redeemed_at ? ` il ${fmtDate(v.redeemed_at)}` : ""}
                        </span>
                      ) : v.available_now ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-sage px-4 py-2 text-xs font-bold uppercase tracking-wider text-sagetext" data-testid={`voucher-status-${v.credit_code}`}>
                          <CheckCircle2 size={14} /> Attivo ora
                        </span>
                      ) : (
                        <div className="flex flex-col items-end gap-2">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-gold/20 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#8a6a24]" data-testid={`voucher-status-${v.credit_code}`}>
                            <Clock size={14} /> Non attivo ora
                          </span>
                          {v.tier !== "Voucher VIP" && (
                            <a
                              href="/#pacchetti"
                              data-testid={`voucher-upgrade-${v.credit_code}`}
                              className="text-xs font-semibold text-terracotta hover:underline"
                            >
                              Ti serve adesso? Passa al Voucher VIP, attivo 7 giorni su 7 →
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
}
